import React from "react";
import { Text } from "@aws-amplify/ui-react";
import styled from "styled-components";

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

// Renders the Mon-Sun weekly-activity strip + total streak counter above the donut charts
// (Task 1 plan, docs/task-1-weekly-activity-streak-plan.md §4.6). Phase 1: a day is "active"
// when the daily Engage AND Grow targets were both met (no Posted condition yet).
//
// Mobile (<=576px, matching the breakpoint already used elsewhere in this app): the strip
// stretches edge-to-edge like a real progress bar, with colored connector segments between
// the points; desktop keeps the original compact, centered layout.

const Wrapper = styled.div`
    margin-top: 0;
    margin-bottom: 1rem;

    @media (max-width: 576px) {
        margin-top: -0.6rem;
        margin-bottom: 1.5rem;
    }
`;

const Track = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;

    @media (max-width: 576px) {
        align-items: flex-start;
        justify-content: space-between;
        gap: 0;
        width: 100%;
    }
`;

const DayColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    flex: 0 0 auto;
`;

const Connector = styled.div`
    display: none;

    @media (max-width: 576px) {
        display: block;
        flex: 1 1 auto;
        min-width: 8px;
        height: 2px;
        margin-top: 0.75rem;
        background: ${(props) => props.$background};
    }
`;

const Dot = styled.div`
    width: 1.6rem;
    height: 1.6rem;
    box-sizing: border-box;
    border-radius: 50%;
    background: ${(props) => props.$background};
    border: ${(props) => props.$border};
    outline: ${(props) => props.$outline};
    outline-offset: 2px;
`;

// A connector segment reads as "accent" only when both days it joins were active (i.e. it's
// part of an unbroken run), "future" as soon as either endpoint hasn't happened yet, and
// "disabled" otherwise (spans a gap in the streak) — all tinted from the same domain colors
// used for the dots, never a flat unthemed grey.
function getConnectorBackground(prevDay, nextDay, colors) {
    if (prevDay.isFuture || nextDay.isFuture) {
        return colors.future;
    }
    if (prevDay.active && nextDay.active) {
        return `linear-gradient(90deg, ${colors.accentStart}, ${colors.accentEnd})`;
    }
    return colors.disabled;
}

export default function WeeklyActivityBar({ weeklyActivity, activityStreak }) {
    if (!weeklyActivity || !Array.isArray(weeklyActivity.days) || weeklyActivity.days.length !== 7) {
        return null;
    }

    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");

    const colors = {
        accentStart: isFirstInfluencerDomain ? "#0074e4" : "#fe60a1",
        accentEnd: isFirstInfluencerDomain ? "#67aaf1" : "#ff8765",
        // Muted, domain-tinted "disabled" state for past days that weren't active — reads as
        // themed, not a generic unbranded grey.
        disabled: isFirstInfluencerDomain
            ? "rgba(0, 116, 228, 0.3)"
            : "rgba(254, 96, 161, 0.3)",
        // Lighter still for days that haven't happened yet.
        future: isFirstInfluencerDomain
            ? "rgba(0, 116, 228, 0.14)"
            : "rgba(254, 96, 161, 0.14)",
    };
    const todayRingColor = colors.accentStart;

    const streakCurrent = activityStreak && typeof activityStreak.current === "number"
        ? activityStreak.current
        : 0;
    const lookbackCappedAt = activityStreak && activityStreak.lookbackCappedAt;
    const streakLabel = lookbackCappedAt && streakCurrent >= lookbackCappedAt
        ? `${lookbackCappedAt}+`
        : `${streakCurrent}`;

    return (
        <Wrapper>
            <Track>
                {weeklyActivity.days.map((dayInfo, index) => (
                    <React.Fragment key={dayInfo.date}>
                        {index > 0 ? (
                            <Connector
                                $background={getConnectorBackground(
                                    weeklyActivity.days[index - 1],
                                    dayInfo,
                                    colors
                                )}
                            />
                        ) : null}
                        <DayColumn>
                            <Dot
                                $background={
                                    dayInfo.isFuture
                                        ? "transparent"
                                        : dayInfo.active
                                            ? `linear-gradient(135deg, ${colors.accentStart}, ${colors.accentEnd})`
                                            : "transparent"
                                }
                                $border={
                                    dayInfo.isFuture
                                        ? `1.5px dashed ${colors.future}`
                                        : dayInfo.active
                                            ? "none"
                                            : `1.5px solid ${colors.disabled}`
                                }
                                $outline={dayInfo.isToday ? `2px solid ${todayRingColor}` : "none"}
                            />
                            <Text fontSize="0.7rem" variation="secondary">
                                {DAY_LABELS[index]}
                            </Text>
                        </DayColumn>
                    </React.Fragment>
                ))}
            </Track>
            <Text
                fontSize="0.75rem"
                variation="secondary"
                textAlign="center"
                style={{ marginTop: "0.35rem" }}
            >
                {weeklyActivity.activeDaysCount}/7 days active this week
                {streakCurrent > 0 ? ` \u00b7 \uD83D\uDD25 ${streakLabel} day streak` : ""}
            </Text>
        </Wrapper>
    );
}
